'use client';
import React from 'react';
import { gsap } from 'gsap';
import Image from '@repo/ui/src/components/Image/New';
import { getButtonVariant } from '@repo/ui/src/components/Button/utils';
import Button from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthSmAndUp } from '@repo/ui/src/styles/variables';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { productClicked } from '@repo/ui/src/services/analytics';
import { AmplienceButtonProps, HeaderVariationProps, ImageProps } from '@repo/ui/src/types';

export interface SeriesHeroProps {
  headingLine1: string;
  headingLine2: string;
  headingLine3: string;
  headerVariation: HeaderVariationProps;
  backgroundImage: ImageProps;
  heroButton: AmplienceButtonProps;
  button: AmplienceButtonProps;
  align: 'left' | 'right' | 'center';
  grillImage: ImageProps;
  badgeImage: ImageProps;
  darkBottom?: boolean;
  order?: number;
}

const SeriesHero = ({
  headingLine1,
  headingLine2,
  headingLine3,
  headerVariation,
  backgroundImage,
  heroButton,
  button = heroButton,
  align,
  grillImage,
  badgeImage,
  darkBottom,
  order,
}: SeriesHeroProps) => {
  const { locale } = useCurrentUrl();
  const heroRef = React.useRef<HTMLDivElement>(null);
  const fadeContainerRef = React.useRef<HTMLDivElement>(null);

  const imageUrl = buildImageSrc(backgroundImage, imageFullWidthSmAndUp, 1, 1);

  React.useEffect(() => {
    const hero = heroRef.current;
    const fadeContainer = fadeContainerRef.current;
    if (!hero || !fadeContainer) return;
    const ctx = gsap.context(() => {
      const seriesBannerTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '50% top',
          scrub: 0.5,
        },
      });
      seriesBannerTimeline.to(fadeContainer, {
        opacity: 1,
      });
    });
    // We can't return revert directly or gsap throws an error
    return () => ctx.revert();
  }, []);

  const HeaderEl = headerVariation?.headerVariation ?? 'h2';

  return (
    <section style={{ zIndex: order }}>
      <div className="sm:min-h-[480px] [clip-path:inset(0_0_0_0)]" ref={heroRef}>
        <div className="fixed top-0 w-full h-screen bg-neutral-700">
          {backgroundImage?.image && (
            <Image
              src={imageUrl}
              alt={backgroundImage.image.name || `${headingLine2 || 'Series'} hero background`}
              fill
              className="object-cover"
              priority
            />
          )}
        </div>
        <div className="fixed inset-0 bg-black" ref={fadeContainerRef} style={{ opacity: 0 }} />
        <div className="h-full w-full top-0 fixed bg-[length:auto_100%] bg-seriesBackground">
          <div
            className={classNames(
              `fixed inset-0 h-screen grid gap-y-2 place-content-center text-center px-6 pt-48 pb-[40vh] sm:pb-[30vh] sm:pt-56 sm:px-[3vw]`,
              align === 'left' && 'justify-start',
              align === 'right' && 'justify-end',
              align !== 'left' && align !== 'right' && 'justify-center',
            )}
          >
            {headingLine1 && (
              <p
                className={classNames(
                  'tra-text-web-display-2-l text-neutral-100 uppercase text-3xl',
                  align === 'left' && 'justify-start',
                  align === 'right' && 'justify-end',
                  align !== 'left' && align !== 'right' && 'justify-center',
                )}
              >
                {headingLine1}
              </p>
            )}
            {headingLine2 && (
              <HeaderEl
                className={classNames(
                  'tra-text-web-display-1-l text-neutral-100 m-0 p-0',
                  align === 'left' && 'justify-start',
                  align === 'right' && 'justify-end',
                  align !== 'left' && align !== 'right' && 'justify-center',
                )}
              >
                {headingLine2}
              </HeaderEl>
            )}
            {headingLine3 && (
              <HeaderEl
                className={classNames(
                  'tra-text-web-display-1 text-neutral-100 m-0 p-0',
                  align === 'left' && 'justify-start',
                  align === 'right' && 'justify-end',
                  align !== 'left' && align !== 'right' && 'justify-center',
                )}
              >
                {headingLine3}
              </HeaderEl>
            )}
            {button?.link && (
              <Button
                as={Anchor}
                link={button.link.link}
                variant={getButtonVariant(button.style, button.icon)}
                className="mt-8 justify-self-center"
                onClick={() => {
                  productClicked({
                    brand: 'Traeger',
                    locale,
                    affiliation: 'website',
                    category: 'grills',
                    description: '',
                    image_url: imageUrl,
                    list_name: 'Series Hero',
                    name: headingLine2,
                    product_id: '', //  not available here
                    sku: '', //  not available here
                    url: window.location.href,
                    basket_token: '',
                    currency: '',
                    price: 0,
                  });
                }}
              >
                {button.link.displayName}
              </Button>
            )}
          </div>
        </div>
        <div
          className={classNames(
            'relative pt-[60vh] pointer-events-none sm:landscape:pt-[70vh] after:absolute after:left-0 after:-bottom-px after:w-full after:h-[30%] after:z-[101] after:bg-speckle after:bg-fixed',
            darkBottom ? 'after:bg-neutral-700' : 'after:bg-warm-100',
          )}
        >
          <div className="my-0 mx-auto w-full px-6 relative landscape:sm:w-[66vw] md:w-[50vw]">
            <Image
              src={buildImageSrc(grillImage, 800, 0, 0)}
              alt={grillImage?.image?.name || `${headingLine2 || 'Series'} grill`}
              width={800}
              height={800}
              className="relative w-full h-auto block z-[102]"
              id="series-grill-image"
            />
            {badgeImage?.image && (
              <Image
                src={buildImageSrc(badgeImage, 104, 0, 0)}
                alt={badgeImage.image.name || 'Product badge'}
                width={104}
                height={104}
                className="absolute top-0 right-0 z-[103] p-6 mr-6"
                id="grill-badge-image"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeriesHero;
